import React, { useState, useEffect } from 'react';
import { useAuth } from '@monorepo/shared-auth';
import { getLeagueMessages, addLeagueMessage } from '../../services/leagueService';
import { IconSend } from '@tabler/icons-react';

const MessagesTab = ({ leagueId }) => {
  const { currentUser } = useAuth();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newMessage, setNewMessage] = useState('');
  const [posting, setPosting] = useState(false);

  useEffect(() => {
    loadMessages();
  }, [leagueId]);

  const loadMessages = async () => {
    try {
      setLoading(true);
      const messagesData = await getLeagueMessages(leagueId);
      setMessages(messagesData.reverse());
    } catch (error) {
      console.error('Error loading messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePostMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !currentUser) return;

    setPosting(true);
    try {
      const userName = currentUser.displayName ||
        `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim() ||
        'Anonymous';

      await addLeagueMessage(leagueId, currentUser.uid, userName, newMessage.trim());
      setNewMessage('');
      loadMessages();
    } catch (error) {
      console.error('Error posting message:', error);
      alert('Failed to post message. Please try again.');
    } finally {
      setPosting(false);
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';

    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    });
  };

  return (
    <section className="ls-messages-tab">
      <header className="ls-messages-header">
        <h2>League Forum</h2>
        <p className="ls-messages-subtitle">
          Public message board for league announcements and discussions
        </p>
      </header>

      <form onSubmit={handlePostMessage} className="ls-message-form">
        <textarea
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          placeholder="Share an announcement or start a discussion..."
          className="ls-message-input"
          rows="3"
          maxLength="500"
          disabled={posting}
        />
        <div className="ls-message-form-footer">
          <span className="ls-char-count">
            {newMessage.length}/500
          </span>
          <button
            type="submit"
            className="ls-btn-post-message"
            disabled={!newMessage.trim() || posting}
          >
            <IconSend size={18} />
            <span>{posting ? 'Posting...' : 'Post'}</span>
          </button>
        </div>
      </form>

      {loading ? (
        <div className="ls-loading-messages">
          <p>Loading messages...</p>
        </div>
      ) : messages.length === 0 ? (
        <div className="ls-empty-messages">
          <p>No messages yet. Be the first to post!</p>
        </div>
      ) : (
        <div className="ls-messages-list">
          {messages.map(message => (
            <article key={message.id} className="ls-message-card">
              <div className="ls-message-header">
                <div className="ls-message-author-avatar">
                  {message.userName?.[0]?.toUpperCase() || 'A'}
                </div>
                <div className="ls-message-meta">
                  <span className="ls-message-author">{message.userName}</span>
                  <span className="ls-message-timestamp">
                    {formatTimestamp(message.timestamp)}
                  </span>
                </div>
              </div>
              <div className="ls-message-content">
                {message.message}
              </div>
            </article>
          ))}
        </div>
      )}
    </section>
  );
};

export default MessagesTab;
